import { t } from "i18next";
import TimeAxis from "../../../../../components/Charts/TimeAxis";

import { useAppStore } from "../../../../../stores/RootStore";
import { DateShift, Equipment, EquipmentAttributesById, EventsCalculationItem, EventsResponseItem, InteractionFromEvents, Event, ParameterViolationWithStart, ParameterViolation } from "../../../../../types/types";
import { DateTime, Duration, Interval } from "luxon";
import { stringToColour } from "../../../../../lib/utils";
import { hexColor } from "../../../../../enums/colors";
import ReactEcharts from "echarts-for-react";
import { Button } from "../../../../../ui/Button";
import { forwardRef, useEffect, useRef, useState } from "react";

import DeviceSensorData from "../../../../devices/deviceItemTabs/deviceNetworkTab/DeviceSensorData";
import { isUndefined, uniqBy } from "lodash";
import { useDimensions } from "../../../../../hooks/useDimensions";
import { TbChevronDown, TbChevronUp, TbPoint, TbPointFilled } from "react-icons/tb";
import { Popover, PopoverTrigger, PopoverContent } from "../../../../../ui/Popover";
import { SummaryUsageRow, UsageRow } from "../../../../../stores/TabsStore";
import _ from "lodash";
import { useFetch } from "../../../../../hooks/useFetch";
import Spinner from "../../../../../ui/Spinner/Spinner";
import TimelineChart from "./TimelineChart";
import { HourHeatMap } from "./HourHeatMap";

import { calculateInteractions, transformEvent } from "../../../../../lib/calculations";

export function EquipmentDay({equipment, data, violations}:{equipment: Equipment, data: SummaryUsageRow, violations: ParameterViolation[]}) {
  return (
    <div className="flex flex-col space-y-2 my-6">
      <div className="grid grid-cols-12 text-sm px-4">
        <div className="col-span-8 flex">
          <div className="font-semibold flex px-6">{t('date')}</div>
        </div>
        
        <div className="flex justify-end col-span-1">{t('worked')}</div>
        <div className="flex justify-end col-span-1">{t('stopped')}</div>
        <div className="flex justify-end col-span-1">{t('turned_off')}</div>
        <div className="flex justify-end col-span-1">{t('total')}</div>
      </div>
      {
        data.data.length > 0 ? _.orderBy(data.data, "dateShift.start_day", "desc").map((day, i) => (
          <div key={i}><DayData equipment={equipment} data={day} violations={violations.filter(v => v.at.toSeconds() >= day.dateShift.start_time.toSeconds() && v.at.toSeconds() <= day.dateShift.end_time.toSeconds())}/></div>
        ))
        :
        <div>{t('no_data')}</div>
      }
    </div>
  )
}

function DayData({equipment, data, violations}:{equipment: Equipment, data: UsageRow, violations: ParameterViolation[]}) {
  const [loading, setLoading] = useState(true)
  const [dayParams, setDayParams] = useState(new URLSearchParams({}));
  const [eventsData, setEventsData] = useState<Event[]>([])
  const [{ response: dayResponse, isLoading: dayLoading, error: dayError }, fetchDayData] =
  useFetch(`/directories/ng_equipment_item_events/events?${dayParams.toString()}`);
  const [open, setOpen] = useState(false)

  let dayViolations = violations.map(v => ({...v, from_start: v.at.toSeconds() - data.dateShift.start_time.toSeconds()})) as ParameterViolationWithStart[]

  useEffect(() => {
    if(!open || eventsData.length > 0) { 
      return 
    }
    setLoading(true)
    setParamsForDay()
    fetchDayData()
  }, [open])

  useEffect(() => {
    if(!dayResponse) { return }
    let response = dayResponse!.data as unknown as EventsResponseItem[]
    let ee = response[0].data.map(e => transformEvent(e, equipment)) as unknown as Event[]
    setEventsData(ee)
    setLoading(false)
  }, [dayResponse])

  function setParamsForDay() {
    let newDayParams = new URLSearchParams({});
    
    newDayParams.append("equipment_item_ids[]", equipment.id)
    newDayParams.append("from", data.dateShift.start_day.startOf('day').toSQL()!);
    newDayParams.append("to", data.dateShift.end_day.endOf('day').toSQL()!);
    newDayParams.append("day_time_from", data.dateShift.start_time.toFormat("HH:mm:ss")!);
    newDayParams.append("day_time_to", data.dateShift.end_time.toFormat("HH:mm:ss")!);

    setDayParams(newDayParams)
  }

  return (
    <div className="border flex flex-col rounded text-sm">
      <div className="w-full flex flex-col">
        <div className="grid grid-cols-12 pl-2 pr-4 py-2">
          <div className="col-span-6 flex flex-col">
            <div className="flex items-center hover:text-primary cursor-pointer" onClick={() => setOpen(!open)}>
              {open ? <TbChevronUp className="w-6" /> : <TbChevronDown className="w-6"/>}
              <div className="font-semibold px-2">{data.dateShift.start_day.toFormat("dd.MM.yyyy")} ({data.dateShift.start_day.weekdayLong})</div>
            </div>
            <div className="pl-8">{data.startWork.isValid ? <>{data.startWork.toFormat("HH:mm:ss")} - {data.endWork.toFormat("HH:mm:ss")}</> : "-"}</div>
          </div>
          <div className="col-span-2 flex flex-col items-end">
            <span className="justify-end">{t('availability')}</span>
            <span className="justify-end">{t('unavailability')}</span>
          </div>

          <div className="col-span-1 flex flex-col items-end">
            <span className="justify-end font-semibold">{data.availability.worked.time}</span>
            <span className="justify-end">{data.unavailability.worked.time}</span>
          </div>
          <div className="col-span-1 flex flex-col items-end">
            <span className="justify-end font-medium">{data.availability.stopped.time}</span>
            <span className="justify-end">{data.unavailability.stopped.time}</span>
          </div>
          <div className="col-span-1 flex flex-col items-end">
            <span className="justify-end">{data.availability.off.time}</span>
            <span className="justify-end">{data.unavailability.off.time}</span>
          </div>
          <div className="col-span-1 flex flex-col items-end">
            <span className="justify-end">{data.availability.total.time}</span>
            <span className="justify-end">{data.unavailability.total.time}</span>
          </div>
        </div>
        {open ? <div className="px-4 pt-4 pb-2 bg-muted border-border border-b last:border-0">
          <div className="flex flex-col space-y-2" >
            <div className="space-y-2 h-min min-h-20 items-center">
              {loading ? <Spinner /> :
                <>
                  <TimelineChart events={eventsData!} shift={data.dateShift} height={16} width="100%"/>
                  {violations.length > 0 && violations ? <ViolationsTimeline violations={dayViolations} periodSeconds={data.dateShift.duration}/> : <></>}
                  <InteractionPersonTimeline events={eventsData} dateShift={data.dateShift}/>
                  
                  <HourHeatMap events={eventsData} shift={data.dateShift} height={40} width="100%"/>
                  <Grpahs equipment={equipment} dateShift={data.dateShift}/>
                </>
              }
            </div> 
          </div>
        </div> : <></>}
      </div>
    </div>

  )
}

function InteractionPersonTimeline({events, dateShift}: {events: Event[]; dateShift: DateShift}) {
  const {persons} = useAppStore()
  let interactions = calculateInteractions(events, dateShift)
  let personIds = _.uniq(interactions.map(i => i.personnel_id))
  const start = interactions && interactions.length > 0 ? DateTime.fromSQL(String(interactions[0].from)) : dateShift.start_day 
  const end = interactions && interactions.length > 0 ? DateTime.fromSQL(String(interactions[interactions.length - 1].to)) : dateShift.end_day 

  const personsConfig: {[id: string]:{name: string, color: string}} = {0: {name: "none", color: hexColor.border}}

  personIds.map(pid => {
    if(pid === 0) {
      personsConfig[Number(pid)] = {name: t('without_person'), color: "rgba(128, 128, 128, 0.5)"}
    } else {
      let person = persons?.find(e => e.id === String(pid))!
      let name = `${person.attributes.last_name!} ${person.attributes.first_name!} ${person.attributes.middle_name!}`
      if(isUndefined(person) || !person) {
        personsConfig[Number(pid)] = {name: t('unknown_person'), color: "rgba(128, 128, 128, 0.5)"}
      } else {
        personsConfig[Number(pid)] = {name: name, color: stringToColour(name)}
      }
      
    }
  })

  const intervals = [
    Interval.fromDateTimes(start, end)
      .splitBy({ hour: 1 })
      .map((d) => d.start?.toISOTime({ includeOffset: false, suppressMilliseconds: true })),
    end.toISOTime({ includeOffset: false, suppressMilliseconds: true }),
  ].flat();

  const shiftDuration = end.toSeconds() - start.toSeconds();

  let graphData: any[] = []
  let style = {
      borderRadius: [50, 50, 50, 50], // Specify the border radius
      borderWidth: 0,
      borderType: 'solid',
      shadowBlur: 0,
      shadowOffsetX: 0,
      shadowOffsetY: 0
  }
  
  

  interactions.map((int, idx) => {
    graphData.push({
      name: personsConfig[Number(int.personnel_id)]?.name,
      type: "bar",
      stack: "timeline",
      color: personsConfig[Number(int.personnel_id)]?.color,
      data: [int.duration],
      itemStyle: style,
    })
  })
    
  const getOptions = () => ({
      tooltip: { 
          show: true
      },
      grid: {
        left: "0%",
        right: "0%",
        bottom: -5,
        top: -5,
        containLabel: false,
      },
      animation: false,
      xAxis: [
        { show: false, max: shiftDuration },
        { show: false, data: intervals },
      ],
      yAxis: { show: false, type: "category" },
      series: graphData,
  });
  
  return interactions.length > 0 && interactions ? <ReactEcharts option={getOptions()} style={{ height: 5, width: "100%" }} opts={{ renderer: "canvas" }} /> : <></>;
}

function ViolationsTimeline({violations, periodSeconds}:{violations: ParameterViolationWithStart[], periodSeconds: number}) {
  const ref = useRef(null);
  const size = useDimensions(ref)
  return (
    <div className="w-full h-4 items-center relative" ref={ref}>
      {violations.map(v => (
        <div style={{position: "absolute", left: `${(size.width / periodSeconds) * v.from_start}px`}}>
          <Popover>
            <PopoverTrigger>
              <TbPointFilled size={12} className="text-red-600" />
            </PopoverTrigger>
            <PopoverContent>
              <div className="text-sm">
                {v.at.toFormat('dd.MM.yyyy HH:mm:ss')}
              </div>
            </PopoverContent>
          </Popover>
          
        </div>
      ))}
    </div>
  )
};

function Grpahs({equipment, dateShift}:{equipment: Equipment, dateShift: DateShift}) {
  const dates = {from: dateShift.start_time.toJSDate(), to: dateShift.end_time.toJSDate()}
  return (
    <div className="flex flex-col gap-2 mt-2">
      {equipment.sensors?.map((sensor) => (
        <DeviceSensorData
          key={sensor.id}
          sensor={sensor.attributes}
          sensorId={sensor.id}
          selectedDate={dates}
          showRfid={false}
        />
      ))}
    </div>
  );
}